import { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { useAppSelector, useAppDispatch } from '@/store/hooks';
import { createTask, fetchBacklogTasks, deleteTask, updateTask, fetchTasks, fetchTask, createSubtask, updateSubtask } from '@/store/taskSlice';
import { sprintAPI } from '@/api/sprintAPI';
import { projectMemberAPI } from '@/api/projectMemberAPI';
import { userAPI } from '@/api/userAPI';
import { mastersAPI, TaskType, Priority, WorkflowStatus } from '@/api/mastersApi';
import { API_BASE_URL } from '@/utils/constants';
import {
  Search,
  Trash2,
  User,
  Filter,
  ChevronDown,
  ChevronRight,
  SlidersHorizontal,
  X as CloseIcon,
  MoreVertical,
  Check
} from 'lucide-react';
import { Sprint, ProjectMember } from '@/types';
import { Loader } from './Loader';
import FilterDrawer from '@/components/FilterDrawer';
import AssigneeChips from '@/components/AssigneeChips';
import { StatusDropdown } from './StatusDropdown';
import { AvatarTooltip } from '@/components/AvatarTooltip';
import { TaskTypeIcon } from '@/components/TaskTypeIcon';
import { PriorityIcon } from '@/components/PriorityIcon';
import { DeleteConfirmDialog } from './DeleteConfirmDialog';
import { ConfirmDialog } from './ConfirmDialog';
import { SprintModal } from './SprintModal';
import { TaskModal } from '@/components/TaskModal/TaskModal';
import SubtaskModal from '@/components/SubtaskModal/SubtaskModal';
import type { Subtask } from '@/types';
import { commentAPI } from '@/api/commentAPI';
import type { Comment, CommentActivity } from '@/types/comments';
import { formatCommentsForActivity } from '@/utils/commentUtils';
import { toPlainText, ensureHtml } from '@/utils/richText';
import { useProjectPermissions } from '@/hooks/useProjectPermissions';
const ASSIGNEE_COLORS = ['bg-blue-500', 'bg-green-500', 'bg-purple-500', 'bg-orange-500', 'bg-pink-500', 'bg-indigo-500', 'bg-teal-500', 'bg-red-500', 'bg-yellow-500', 'bg-cyan-500'];

const getAssigneeColor = (seed: string) => {
  const source = seed || 'user';
  const sum = Array.from(source).reduce((acc, ch) => acc + ch.charCodeAt(0), 0);
  return ASSIGNEE_COLORS[sum % ASSIGNEE_COLORS.length];
};

const getInitials = (name?: string) => {
  if (!name) return '';
  const parts = name.trim().split(/\s+/);
  if (parts.length === 1) return parts[0].substring(0, 2).toUpperCase();
  return (parts[0][0] + parts[1][0]).toUpperCase();
};

export const BacklogView = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedAssigneeIds, setSelectedAssigneeIds] = useState<string[]>([]);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [selectedTasks, setSelectedTasks] = useState<Set<string>>(new Set());
  const [taskTypes, setTaskTypes] = useState<TaskType[]>([]);
  const [priorities, setPriorities] = useState<Priority[]>([]);
  const [workflowStatuses, setWorkflowStatuses] = useState<WorkflowStatus[]>([]);
  const [newTask, setNewTask] = useState({
    title: '',
    description: '',
    task_type_id: '',
    priority_id: '',
    assignee: '',
    labels: [] as string[]
  });
  const [showAssigneeDropdown, setShowAssigneeDropdown] = useState<string | null>(null);
  const [assigneeSearch, setAssigneeSearch] = useState('');
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [taskToDelete, setTaskToDelete] = useState<string | null>(null);
  // Inline title editing state
  const [editingTaskId, setEditingTaskId] = useState<string | null>(null);
  const [editingTitle, setEditingTitle] = useState<string>('');
  const [collapsedSectionKeys, setCollapsedSectionKeys] = useState<Set<string>>(new Set());
  const [creatingSectionKey, setCreatingSectionKey] = useState<string | null>(null);
  const [createTitle, setCreateTitle] = useState<string>('');
  const [createTypeId, setCreateTypeId] = useState<string | null>(null);
  const [createAssigneeId, setCreateAssigneeId] = useState<string | null>(null);
  const [createTypeOpen, setCreateTypeOpen] = useState<boolean>(false);

  // Sprint name editing
  const [editingSprintId, setEditingSprintId] = useState<string | null>(null);
  const [editingSprintName, setEditingSprintName] = useState<string>('');
  const [sprintModalOpen, setSprintModalOpen] = useState(false);
  const [sprintToEdit, setSprintToEdit] = useState<Sprint | null>(null);
  const [sprintMenuOpenId, setSprintMenuOpenId] = useState<string | null>(null);
  const [sprintToDelete, setSprintToDelete] = useState<string | null>(null);
  const [showSprintDeleteConfirm, setShowSprintDeleteConfirm] = useState(false);
  const [isTaskModalOpen, setIsTaskModalOpen] = useState(false);
  const [openedTask, setOpenedTask] = useState<any | null>(null);
  const [showCompleteConfirm, setShowCompleteConfirm] = useState(false);
  const [completeSprintId, setCompleteSprintId] = useState<string | null>(null);
  const [comments, setComments] = useState<Comment[]>([]);
  const [activity, setActivity] = useState<CommentActivity[]>([]);
  const [draggingTaskId, setDraggingTaskId] = useState<string | null>(null);
  const [dragOverSectionKey, setDragOverSectionKey] = useState<string | null>(null);
  
  // Subtask Modal States
  const [activeSubtask, setActiveSubtask] = useState<Subtask | null>(null);
  const [isSubtaskModalOpen, setIsSubtaskModalOpen] = useState(false);
  const [parentTaskForSubtask, setParentTaskForSubtask] = useState<any>(null);

  useEffect(() => {
    const close = () => setSprintMenuOpenId(null);
    window.addEventListener('click', close);
    return () => window.removeEventListener('click', close);
  }, []);

  // Disable page scrollbar while BacklogView is mounted
  useEffect(() => {
    const html = document.documentElement;
    const prevHtmlOverflow = html.style.overflow;
    const prevBodyOverflow = document.body.style.overflow;
    const prevHtmlHeight = html.style.height;
    const prevBodyHeight = document.body.style.height;
    html.style.overflow = 'hidden';
    document.body.style.overflow = 'hidden';
    html.style.height = '100vh';
    document.body.style.height = '100vh';
    return () => {
      html.style.overflow = prevHtmlOverflow;
      document.body.style.overflow = prevBodyOverflow;
      html.style.height = prevHtmlHeight;
      document.body.style.height = prevBodyHeight;
    };
  }, []);

  const moveSprintInState = (sprintId: string, direction: 'top' | 'bottom' | 'up' | 'down') => {
    setSprints((prev) => {
      const idx = prev.findIndex((s) => String(s.id) === String(sprintId));
      if (idx === -1) return prev;
      const newOrder = [...prev];
      const [item] = newOrder.splice(idx, 1);
      let newIndex = idx;
      if (direction === 'top') newIndex = 0;
      if (direction === 'bottom') newIndex = newOrder.length;
      if (direction === 'up') newIndex = Math.max(0, idx - 1);
      if (direction === 'down') newIndex = Math.min(newOrder.length, idx + 1);
      newOrder.splice(newIndex, 0, item);
      return newOrder;
    });
  };

  const handleConfirmDeleteSprint = async () => {
    if (!sprintToDelete || !currentProject?.id) return;
    try {
      await sprintAPI.deleteSprint(sprintToDelete);
      const data = await sprintAPI.getSprints(currentProject.id);
      setSprints(data);
      setShowSprintDeleteConfirm(false);
      setSprintToDelete(null);
      // refresh tasks/backlog
      dispatch(fetchBacklogTasks(currentProject.id));
      dispatch(fetchTasks({ project_id: currentProject.id }));
    } catch (e) {
      console.error('Failed to delete sprint', e);
      alert('Failed to delete sprint. Please try again.');
    }
  };

  // View settings panel state
  const [isSettingsOpen, setIsSettingsOpen] = useState(false);
  const settingsBtnRef = useRef<HTMLButtonElement | null>(null);
  const [showEpicPanel, setShowEpicPanel] = useState<boolean>(false);
  const [showEmptySprints, setShowEmptySprints] = useState<boolean>(true);
  const [compactDensity, setCompactDensity] = useState<boolean>(true);
  const [fieldWorkType, setFieldWorkType] = useState<boolean>(true);
  const [fieldWorkItemKey, setFieldWorkItemKey] = useState<boolean>(true);
  const [fieldEpic, setFieldEpic] = useState<boolean>(false);
  const [fieldDueDate, setFieldDueDate] = useState<boolean>(false);
  const [fieldStatus, setFieldStatus] = useState<boolean>(true);
  const [fieldEstimate, setFieldEstimate] = useState<boolean>(false);
  const [fieldAssignee, setFieldAssignee] = useState<boolean>(true);

  // Persist settings
  useEffect(() => {
    try {
      const raw = localStorage.getItem('backlog_view_settings');
      if (raw) {
        const s = JSON.parse(raw);
        setShowEpicPanel(!!s.showEpicPanel);
        setShowEmptySprints(s.showEmptySprints !== false);
        setCompactDensity(!!s.compactDensity);
        setFieldWorkType(s.fieldWorkType !== false);
        setFieldWorkItemKey(s.fieldWorkItemKey !== false);
        setFieldEpic(!!s.fieldEpic);
        setFieldDueDate(!!s.fieldDueDate);
        setFieldStatus(s.fieldStatus !== false);
        setFieldEstimate(!!s.fieldEstimate);
        setFieldAssignee(s.fieldAssignee !== false);
      }
    } catch { }
  }, []);
  useEffect(() => {
    const s = {
      showEpicPanel,
      showEmptySprints,
      compactDensity,
      fieldWorkType,
      fieldWorkItemKey,
      fieldEpic,
      fieldDueDate,
      fieldStatus,
      fieldEstimate,
      fieldAssignee,
    };
    try { localStorage.setItem('backlog_view_settings', JSON.stringify(s)); } catch { }
  }, [showEpicPanel, showEmptySprints, compactDensity, fieldWorkType, fieldWorkItemKey, fieldEpic, fieldDueDate, fieldStatus, fieldEstimate, fieldAssignee]);

  const { currentProject } = useAppSelector((state) => state.projects);
  const projectKey = currentProject?.key || 'PRJ';
  const { user } = useAppSelector((state) => state.auth);
  const currentUserId = user?.id ? String(user.id) : null;
  const currentUserName = user?.name || user?.display_name || [user?.first_name, user?.last_name].filter(Boolean).join(' ').trim() || user?.username || user?.email || 'You';
  const currentUserInitials = getInitials(currentUserName);
  const currentUserColor = getAssigneeColor(currentUserId || currentUserName);
  const { tasks, loading } = useAppSelector((state) => state.tasks);
  const [sprints, setSprints] = useState<Sprint[]>([]);
  const [members, setMembers] = useState<ProjectMember[]>([]);
  const [orgUsers, setOrgUsers] = useState<ProjectMember[]>([]);
  const dispatch = useAppDispatch();
  // Gate initial render until all boot API calls finish
  const [bootLoading, setBootLoading] = useState<boolean>(true);
  const [mastersLoaded, setMastersLoaded] = useState(false);
  const [membersLoadedFlag, setMembersLoadedFlag] = useState(false);
  const [sprintsLoadedFlag, setSprintsLoadedFlag] = useState(false);

  // Assignee overflow handled inside AssigneeChips and task row dropdowns

  // Merge project members with org users (project members take precedence)
  const combinedMembers = useMemo(() => {
    const byId: Record<string, ProjectMember> = {} as any;
    for (const u of orgUsers) byId[u.user_id] = u;
    for (const m of members) byId[m.user_id] = m; // override with project member details if present
    return Object.values(byId);
  }, [members, orgUsers]);

  // Project-level permissions: viewer should be read-only within that project
    const { isViewerOnly } = useProjectPermissions(currentProject?.id || null);
  
  // Fetch tasks when component mounts or project changes
  useEffect(() => {
    if (currentProject?.id) {
      setBootLoading(true);
      dispatch(fetchTasks({ project_id: currentProject.id }));
      (async () => {
        try {
          const data = await sprintAPI.getSprints(currentProject.id);
          setSprints(data);
          setSprintsLoadedFlag(true);
        } catch { }
        try {
          const projMembers = await projectMemberAPI.getProjectMembers(currentProject.id);
          setMembers(projMembers);
          setMembersLoadedFlag(true);
        } catch { }
        try {
          // Fallback: load organization users if project has no members
          if (user?.organization_id) {
            const users = await userAPI.getUsers({ organization_id: user.organization_id, is_active: true, limit: 100 });
            const mapped = users.map((u: any) => ({
              id: `org-${u.id}`,
              project_id: currentProject.id,
              user_id: u.id,
              role: 'member',
              is_active: u.is_active,
              joined_at: '',
              created_at: '',
              updated_at: '',
              username: u.username,
              user_email: u.email,
              user_display_name: u.display_name || `${u.first_name} ${u.last_name}`.trim(),
              user_avatar_url: u.avatar_url,
            }));
            setOrgUsers(mapped as any);
            setMembersLoadedFlag(true);
          }
        } catch { }
        // Fetch task types, priorities, and workflow statuses in parallel for faster loading
        // Prioritize workflow statuses by loading them first, then load others in parallel
        const [statusesResult, typesResult, priosResult] = await Promise.allSettled([
          mastersAPI.getWorkflowStatuses().catch(() => []),
          mastersAPI.getTaskTypes().catch(() => []),
          mastersAPI.getPriorities().catch(() => [])
        ]);

        // Set workflow statuses first (needed for immediate display)
        if (statusesResult.status === 'fulfilled' && statusesResult.value) {
          setWorkflowStatuses(statusesResult.value.sort((a, b) => a.order - b.order));
        }

        // Set task types
        if (typesResult.status === 'fulfilled' && typesResult.value) {
          setTaskTypes(typesResult.value.filter(t => t.is_active !== false));
        }

        // Set priorities
        if (priosResult.status === 'fulfilled' && priosResult.value) {
          setPriorities(priosResult.value.filter(p => p.is_active !== false).sort((a, b) => a.order - b.order));
        }
        setMastersLoaded(true);
        setBootLoading(false);
      })();
    }
  }, [currentProject?.id, dispatch]);

  // Set default task type and priority when masters are loaded
  useEffect(() => {
    if (taskTypes.length > 0) {
      setNewTask(prev => {
        if (!prev.task_type_id) {
          const defaultType = taskTypes.find(t => t.name.toLowerCase() === 'task');
          if (defaultType) {
            return { ...prev, task_type_id: defaultType.id };
          }
        }
        return prev;
      });
    }
  }, [taskTypes]);

  useEffect(() => {
    if (priorities.length > 0) {
      setNewTask(prev => {
        if (!prev.priority_id) {
          const defaultPrio = priorities.find(p => p.key === 'medium');
          if (defaultPrio) {
            return { ...prev, priority_id: defaultPrio.id };
          }
        }
        return prev;
      });
    }
  }, [priorities]);

  // Removed legacy overflow positioning state/effects

  // JIRA-like Filter drawer state
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const filterBtnWrapRef = useRef<HTMLDivElement | null>(null);

  const [selectedStatusIds, setSelectedStatusIds] = useState<string[]>([]);
  const [selectedPriorityIds, setSelectedPriorityIds] = useState<string[]>([]);
  const [selectedTaskTypeIds, setSelectedTaskTypeIds] = useState<string[]>([]);
  const [selectedSprintIds, setSelectedSprintIds] = useState<string[]>([]);
  const [selectedLabels, setSelectedLabels] = useState<string[]>([]);
  const [parentOnly, setParentOnly] = useState<boolean>(false);
  const [subtaskOnly, setSubtaskOnly] = useState<boolean>(false);
  // Project member role state
  const [projectMemberRole, setProjectMemberRole] = useState<string | null>(null);

  // Load current user's project member role
  const loadProjectMemberRole = useCallback(async () => {

    try {

      if (!currentUserId || !currentProject?.id) return;
      const membership = await projectMemberAPI.checkMembership(currentProject?.id, currentUserId);
      setProjectMemberRole(membership.is_member ? (membership.member?.role || null) : null);
    } catch (error) {
      console.error('Failed to load project member role:', error);
      setProjectMemberRole(null);
    }
  }, [currentProject?.id, currentUserId]);

  useEffect(() => {
    if (currentProject?.id) {
      loadProjectMemberRole();
    }
  }, [currentProject?.id, loadProjectMemberRole]);

  // Check if user has sprint admin permissions
  // User has permission if:
  // 1. Main role is 'admin' OR
  // 2. Main role is 'developer' AND project role is 'admin'
  const hasSprintAdminPermission = useMemo((): boolean => {
    if (!user) return false;

    const mainRole = user.role?.toLowerCase();
    const projectRole = projectMemberRole?.toLowerCase();

    // Main admin role always has permission
    if (mainRole === 'admin' || mainRole === 'super_admin') {
      return true;
    }

    // Developer with project admin role has permission
    if (mainRole === 'developer' && projectRole === 'admin') {
      return true;
    }

    return false;
  }, [user, projectMemberRole]);

  // FilterDrawer handles its own positioning and outside-click via anchorRef

  const handleAssigneeToggle = (userId: string) => {
    setSelectedAssigneeIds(prev =>
      prev.includes(userId)
        ? prev.filter(a => a !== userId)
        : [...prev, userId]
    );
  };

  const handleCreateTask = async () => {
    if (newTask.title.trim().length >= 3) {
      const taskData: any = {
        title: newTask.title.trim(),
        description: newTask.description || undefined,
        project_id: currentProject?.id || '',
        labels: newTask.labels.length > 0 ? newTask.labels.join(', ') : undefined,
        assigned_to: newTask.assignee || undefined
      };

      // Add master IDs if selected
      if (newTask.task_type_id) {
        taskData.task_type_id = newTask.task_type_id;
      }
      if (newTask.priority_id) {
        taskData.priority_id = newTask.priority_id;
      }

      // Set default workflow_status_id to "todo" (first initial status or status named "todo"/"to do")
      if (!taskData.workflow_status_id) {
        const todoStatus = workflowStatuses.find(s =>
          s.is_initial ||
          s.name.toLowerCase() === 'todo' ||
          s.name.toLowerCase() === 'to do' ||
          s.category === 'todo'
        );
        if (todoStatus) {
          taskData.workflow_status_id = todoStatus.id;
        } else if (workflowStatuses.length > 0) {
          // Fallback to first status if no "todo" status found
          taskData.workflow_status_id = workflowStatuses[0].id;
        }
      }

      await dispatch(createTask(taskData)).unwrap();
      // Refresh both backlog and board tasks after creation
      if (currentProject?.id) {
        dispatch(fetchBacklogTasks(currentProject.id));
        dispatch(fetchTasks({ project_id: currentProject.id })); // Also refresh board tasks
      }

      // Reset form with defaults
      const defaultType = taskTypes.find(t => t.name.toLowerCase() === 'task');
      const defaultPrio = priorities.find(p => p.key === 'medium');
      setNewTask({
        title: '',
        description: '',
        task_type_id: defaultType?.id || '',
        priority_id: defaultPrio?.id || '',
        assignee: '',
        labels: []
      });
      setShowCreateModal(false);
    } else {
      alert('Task title must be at least 3 characters long');
    }
  };

  const handleCompleteSprint = async (sprintId: string) => {
    try {
      await sprintAPI.completeSprint(sprintId);
      // Refresh sprints and tasks
      if (currentProject?.id) {
        const data = await sprintAPI.getSprints(currentProject.id);
        setSprints(data);
        dispatch(fetchBacklogTasks(currentProject.id));
        dispatch(fetchTasks({ project_id: currentProject.id }));
      }
      setShowCompleteConfirm(false);
      setCompleteSprintId(null);
    } catch (error) {
      console.error('Failed to complete sprint:', error);
      alert('Failed to complete sprint. Please try again.');
    }
  };

  const handleStartSprint = async (sprintId: string) => {
    if (!currentProject?.id) return;
    try {
      await sprintAPI.startSprint(sprintId);
      // Refresh sprints and tasks
      const data = await sprintAPI.getSprints(currentProject.id);
      setSprints(data);
      dispatch(fetchBacklogTasks(currentProject.id));
      dispatch(fetchTasks({ project_id: currentProject.id }));
    } catch (error) {
      console.error('Failed to start sprint:', error);
      alert('Failed to start sprint. Please try again.');
    }
  };

  const handleCreateSprint = async () => {
    if (!currentProject?.id) return;
    try {
      const name = `New sprint`;
      const created = await sprintAPI.createSprint({ name, project_id: currentProject.id });
      // Optimistically add and open inline create
      setSprints((prev) => [created, ...prev]);
      setCollapsedSectionKeys((prev) => {
        const next = new Set(prev);
        next.delete(created.id as any);
        return next;
      });
      setEditingSprintId(created.id as any);
      setEditingSprintName(created.name || 'New sprint');
      setCreatingSectionKey(created.id as any);
    } catch (error) {
      console.error('Failed to create sprint:', error);
      alert('Failed to create sprint. Please try again.');
    }
  };



  const handleDeleteSelectedTasks = async () => {
    if (selectedTasks.size === 0) return;

    setTaskToDelete(null); // Clear any single task to delete
    setShowDeleteConfirm(true);
  };

  const handleTaskSelection = (taskId: string, checked: boolean) => {
    const newSelected = new Set(selectedTasks);
    if (checked) {
      newSelected.add(taskId);
    } else {
      newSelected.delete(taskId);
    }
    setSelectedTasks(newSelected);
  };

  const handleConfirmDeleteTask = async () => {
    if (!taskToDelete) return;

    try {
      await dispatch(deleteTask(taskToDelete)).unwrap();
      // Refresh backlog tasks after deletion
      if (currentProject?.id) {
        dispatch(fetchBacklogTasks(currentProject.id));
      }
      setShowDeleteConfirm(false);
      setTaskToDelete(null);
      setSelectedTasks(new Set()); // Clear all selected tasks after successful deletion
    } catch (error) {
      console.error('Failed to delete task:', error);
      alert('Failed to delete task. Please try again.');
    }
  };

  const handleConfirmDeleteSelected = async () => {
    if (selectedTasks.size === 0) return;
    try {
      const ids = Array.from(selectedTasks);
      await Promise.all(ids.map((id) => dispatch(deleteTask(id)).unwrap()));
      if (currentProject?.id) {
        dispatch(fetchBacklogTasks(currentProject.id));
        dispatch(fetchTasks({ project_id: currentProject.id }));
      }
      setSelectedTasks(new Set());
      setShowDeleteConfirm(false);
      setTaskToDelete(null);
    } catch (error) {
      console.error('Failed to delete selected tasks:', error);
      alert('Failed to delete selected tasks. Please try again.');
    }
  };


  const handleAssigneeChange = async (taskId: string, newAssigneeId: string | null) => {
    try {
      await dispatch(updateTask({
        id: taskId,
        data: { assigned_to: newAssigneeId === null ? null : newAssigneeId }
      })).unwrap();

      // Refresh backlog tasks and tasks after update
      if (currentProject?.id) {
        await dispatch(fetchBacklogTasks(currentProject.id));
        await dispatch(fetchTasks({ project_id: currentProject.id }));
      }
      await dispatch(fetchTask(taskId));
      
      // Refresh project members after assignee update
      if (currentProject?.id) {
        try {
          const projMembers = await projectMemberAPI.getProjectMembers(currentProject.id);
          setMembers(projMembers);
        } catch (error) {
          console.error('Failed to refresh project members:', error);
        }
      }
      
      // Update openedTask if it matches
      if (openedTask?.id === taskId) {
        try {
          const fresh = await dispatch(fetchTask(taskId)).unwrap();
          setOpenedTask(fresh);
        } catch { /* ignore */ }
      }
      
      setShowAssigneeDropdown(null);
    } catch (error) {
      console.error('Failed to update assignee:', error);
    }
  };

  const handleReporterChange = async (taskId: string, newReporterId: string | null) => {
    try {
      await dispatch(updateTask({
        id: taskId,
        data: { reporter_id: newReporterId === null ? null : newReporterId }
      })).unwrap();

      // Refresh backlog tasks and tasks after update
      if (currentProject?.id) {
        await dispatch(fetchBacklogTasks(currentProject.id));
        await dispatch(fetchTasks({ project_id: currentProject.id }));
      }
      await dispatch(fetchTask(taskId));
      
      // Refresh project members after reporter update
      if (currentProject?.id) {
        try {
          const projMembers = await projectMemberAPI.getProjectMembers(currentProject.id);
          setMembers(projMembers);
        } catch (error) {
          console.error('Failed to refresh project members:', error);
        }
      }
      
      // Update openedTask if it matches
      if (openedTask?.id === taskId) {
        try {
          const fresh = await dispatch(fetchTask(taskId)).unwrap();
          setOpenedTask(fresh);
        } catch { /* ignore */ }
      }
    } catch (error) {
      console.error('Failed to update reporter:', error);
    }
  };

  const handleStatusChange = async (taskId: string, workflowStatusId: string) => {
    try {
      await dispatch(updateTask({
        id: taskId,
        data: { workflow_status_id: workflowStatusId || undefined }
      })).unwrap();
      
      if (currentProject?.id) {
        // Refresh tasks lists
        await dispatch(fetchTasks({ project_id: currentProject.id }));
        await dispatch(fetchBacklogTasks(currentProject.id));
        // Fetch the individual task to update currentTask in Redux
        await dispatch(fetchTask(taskId));
      } else {
        await dispatch(fetchTasks({}));
        await dispatch(fetchTask(taskId));
      }
    } catch (error) {
      console.error('Failed to update task status:', error);
    }
  };

  // Sync openedTask with updated tasks after status change (following pattern from other components)
  useEffect(() => {
    if (openedTask && isTaskModalOpen) {
      const updatedTask = tasks.find((t: any) => t.id === openedTask.id);
      if (updatedTask && updatedTask.workflow_status_id !== openedTask.workflow_status_id) {
        setOpenedTask(updatedTask);
      }
    }
  }, [tasks, openedTask?.id, isTaskModalOpen]);

  // Use project tasks; backlog will be the unscheduled group
  const allTasks = useMemo(() => {
    const projectId = currentProject?.id;
    return tasks.filter(t => !projectId || t.project_id === projectId);
  }, [tasks, currentProject?.id]);


  // Derive all labels for filter drawer
  const allLabels = useMemo(() => {
    const set = new Set<string>();
    allTasks.forEach((t: any) => {
      const raw = t?.labels;
      const labels = Array.isArray(raw) ? raw : (typeof raw === 'string' ? raw.split(',').map((s: string) => s.trim()).filter(Boolean) : []);
      labels.forEach((l: string) => set.add(l));
    });
    return Array.from(set).sort((a, b) => a.localeCompare(b));
  }, [allTasks]);

  const filteredTasks = allTasks.filter(task => {
    const matchesSearch = task.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      task.id?.toString().toLowerCase().includes(searchQuery.toLowerCase());
    const matchesAssignee = selectedAssigneeIds.length === 0 ||
      (task.assigned_to ? selectedAssigneeIds.includes(task.assigned_to.toString()) : false);
    if (!matchesSearch || !matchesAssignee) return false;
    const byId = (v?: string) => (v ? String(v) : '');
    if (parentOnly && (task as any).parent_id) return false;
    if (subtaskOnly && !(task as any).parent_id) return false;
    if (selectedSprintIds.length > 0 && !selectedSprintIds.includes(byId((task as any).sprint_id))) return false;
    if (selectedPriorityIds.length > 0 && !selectedPriorityIds.includes(byId((task as any).priority_id))) return false;
    if (selectedTaskTypeIds.length > 0 && !selectedTaskTypeIds.includes(byId((task as any).task_type_id))) return false;
    if (selectedStatusIds.length > 0) {
      const sid = byId((task as any).workflow_status_id);
      if (sid) {
        if (!selectedStatusIds.includes(sid)) return false;
      } else if ((task as any).status) {
        const legacy = String((task as any).status).toLowerCase();
        const nameMatch = workflowStatuses.find((s) => selectedStatusIds.includes(String((s as any).id)) && (s.name || '').toLowerCase().includes(legacy));
        if (!nameMatch) return false;
      } else {
        return false;
      }
    }
    if (selectedLabels.length > 0) {
      const raw = (task as any).labels;
      const labels = Array.isArray(raw) ? raw : (typeof raw === 'string' ? raw.split(',').map((s: string) => s.trim()).filter(Boolean) : []);
      const hasAny = labels.some((l: string) => selectedLabels.includes(l));
      if (!hasAny) return false;
    }
    return true;
  });

  // Build sections by sprint (active sprints first) + backlog (no sprint)
  const sections = useMemo(() => {
    const sprintIdToName: Record<string, string> = {};
    for (const s of sprints) sprintIdToName[s.id] = s.name;
    const grouped: Record<string, typeof filteredTasks> = {} as any;
    for (const t of filteredTasks) {
      const key = t.sprint_id || 'unscheduled';
      if (!grouped[key]) (grouped as any)[key] = [] as any;
      (grouped[key] as any).push(t);
    }
    const result: { key: string; name: string; tasks: typeof filteredTasks; isBacklog: boolean; isActive?: boolean; storyPoints?: number }[] = [];
    if (grouped['unscheduled']) {
      const storyPoints = grouped['unscheduled'].reduce((sum, task) => sum + (task.story_points || 0), 0);
      result.push({
        key: 'unscheduled',
        name: 'Backlog (No Sprint)',
        tasks: grouped['unscheduled'],
        isBacklog: true,
        storyPoints
      });
    }
    const orderedSprints = [...sprints].sort((a, b) => Number(b.is_active) - Number(a.is_active));
    for (const s of orderedSprints) {
      const list = grouped[s.id] || [] as any;
      const storyPoints = list.reduce((sum, task) => sum + (task.story_points || 0), 0);
      result.push({
        key: s.id,
        name: s.name,
        tasks: list,
        isBacklog: false,
        isActive: s.is_active,
        storyPoints
      });
    }
    // Include any sprint ids present in tasks but missing from API list
    for (const key of Object.keys(grouped)) {
      if (key !== 'unscheduled' && !sprintIdToName[key]) {
        const storyPoints = grouped[key].reduce((sum, task) => sum + (task.story_points || 0), 0);
        result.push({
          key,
          name: 'Sprint',
          tasks: grouped[key],
          isBacklog: false,
          storyPoints
        });
      }
    }
    // Optionally hide empty sprints
    const filtered = showEmptySprints ? result : result.filter(r => r.isBacklog || (r.tasks && r.tasks.length > 0));
    return filtered;
  }, [filteredTasks, sprints, showEmptySprints]);

  const loadComments = async (taskId: string) => {
    try {
      const c = await commentAPI.getComments(taskId);
      setComments(c);
      setActivity(formatCommentsForActivity(c));
    } catch {
      setComments([]);
      setActivity([]);
    }
  };

  const handleTaskDragStart = (event: React.DragEvent<HTMLDivElement>, task: any, sectionKey: string) => {
    try {
      event.dataTransfer.setData('text/plain', String(task.id));
      event.dataTransfer.effectAllowed = 'move';
    } catch {
      // Silently ignore DataTransfer errors
    }
    setDraggingTaskId(String(task.id));
    setDragOverSectionKey(sectionKey);
  };

  const handleTaskDragEnd = () => {
    setDraggingTaskId(null);
    setDragOverSectionKey(null);
  };

  const handleSectionDragOver = (event: React.DragEvent<HTMLDivElement>, sectionKey: string) => {
    event.preventDefault();
    event.stopPropagation();
    try {
      if (event.dataTransfer) {
        event.dataTransfer.dropEffect = 'move';
      }
    } catch {
      // Ignore
    }
    if (dragOverSectionKey !== sectionKey) {
      setDragOverSectionKey(sectionKey);
    }
  };

  const handleSectionDragLeave = (_event: React.DragEvent<HTMLDivElement>, sectionKey: string) => {
    setDragOverSectionKey((prev) => (prev === sectionKey ? null : prev));
  };

  const handleSectionDrop = async (event: React.DragEvent<HTMLDivElement>, targetSectionKey: string) => {
    event.preventDefault();
    event.stopPropagation();
    let taskId = '';
    try {
      taskId = event.dataTransfer.getData('text/plain');
    } catch {
      // Ignore
    }
    if (!taskId && draggingTaskId) {
      taskId = draggingTaskId;
    }

    setDragOverSectionKey(null);

    if (!taskId) {
      setDraggingTaskId(null);
      return;
    }

    const task = allTasks.find((t: any) => String(t.id) === String(taskId));
    if (!task) {
      setDraggingTaskId(null);
      return;
    }

    const currentSectionKey = task.sprint_id ? String(task.sprint_id) : 'unscheduled';
    if (currentSectionKey === targetSectionKey) {
      setDraggingTaskId(null);
      return;
    }

    const payload: any = {
      sprint_id: targetSectionKey === 'unscheduled' ? null : targetSectionKey
    };

    try {
      await dispatch(updateTask({ id: String(task.id), data: payload })).unwrap();
      if (currentProject?.id) {
        await dispatch(fetchTasks({ project_id: currentProject.id }));
        await dispatch(fetchBacklogTasks(currentProject.id));
      } else {
        await dispatch(fetchTasks({}));
      }
    } catch (error) {
      console.error('Failed to move task:', error);
    } finally {
      setDraggingTaskId(null);
    }
  };

  const getMemberById = (id?: string) => {
    if (!id) return undefined;
    // Try exact match first
    let member = combinedMembers.find(m => m.user_id === id);
    if (member) return member;
    // Try string comparison (in case of type mismatch)
    const idStr = String(id);
    member = combinedMembers.find(m => String(m.user_id) === idStr);
    if (member) return member;
    // Debug: log if not found (can remove later)
    if (combinedMembers.length > 0 && id) {
      console.debug('Assignee not found:', {
        assignedToId: id,
        availableUserIds: combinedMembers.map(m => m.user_id),
        membersCount: combinedMembers.length
      });
    }
    return undefined;
  };

  const resolveTaskTypeIconUrl = (raw?: string | null) => {
    if (!raw) return null;
    const builtInIcons = ['task', 'bug', 'story', 'epic', 'subtask'];
    if (builtInIcons.includes(raw.toLowerCase())) return null;
    try {
      if (raw.startsWith('data:') || raw.startsWith('http://') || raw.startsWith('https://')) return raw;
      // Build from API origin so '/static/..' and 'static/..' both resolve correctly
      const api = new URL(API_BASE_URL);
      const origin = `${api.protocol}//${api.host}`; // e.g., http://localhost:8000
      const path = raw.startsWith('/') ? raw : `/${raw}`;
      return origin + path;
    } catch {
      return null;
    }
  };

  const ToggleSwitch = ({ checked, onChange, className = '' }: { checked: boolean; onChange: (value: boolean) => void; className?: string }) => {
    return (
      <button
        type="button"
        aria-pressed={checked}
        onClick={() => onChange(!checked)}
        className={`relative inline-flex items-center h-5 w-10 rounded-full transition-colors ${checked ? 'bg-green-700' : 'bg-gray-300'} ${className}`}
      >
        <span
          className={`inline-block h-4 w-4 bg-white rounded-full transform transition-transform ${checked ? 'translate-x-5' : 'translate-x-1'}`}
        />
      </button>
    );
  };

  const beginCreateInSection = (secKey: string) => {
    setCreatingSectionKey(secKey);
    setCreateTitle('');
    const defaultType = taskTypes.find(t => (t.name || '').toLowerCase() === 'task') || taskTypes[0];
    setCreateTypeId(defaultType?.id ? String(defaultType.id) : null);
    setCreateAssigneeId(null);
    setCreateTypeOpen(false);
  };

  const cancelCreate = () => {
    setCreatingSectionKey(null);
    setCreateTitle('');
    setCreateAssigneeId(null);
    setCreateTypeId(null);
    setCreateTypeOpen(false);
  };

  const saveCreateInSection = async () => {
    if (!currentProject?.id || !creatingSectionKey || !createTitle.trim()) return;
    try {
      const defaultPrio = priorities.find(p => (p.key || '').toLowerCase() === 'medium') || priorities[0];
      const initialStatus = workflowStatuses.find(s => s.is_initial) || workflowStatuses[0];
      const taskData: any = {
        title: createTitle.trim(),
        project_id: currentProject.id,
      };
      if (createTypeId) taskData.task_type_id = createTypeId;
      if (createAssigneeId) taskData.assigned_to = createAssigneeId;
      if (defaultPrio?.id) taskData.priority_id = defaultPrio.id;
      if (initialStatus?.id) taskData.workflow_status_id = initialStatus.id;
      if (creatingSectionKey !== 'unscheduled') taskData.sprint_id = creatingSectionKey;

      await dispatch(createTask(taskData)).unwrap();
      dispatch(fetchTasks({ project_id: currentProject.id }));
      dispatch(fetchBacklogTasks(currentProject.id));
      cancelCreate();
    } catch (e) {
      console.error('Create task failed', e);
      alert('Failed to create task. Please try again.');
    }
  };



  // Insets are not needed in grid layout
  const centerScrollRef = useRef<HTMLDivElement | null>(null);
  const [centerHeight, setCenterHeight] = useState<number>(0);

  const recomputeCenterHeight = () => {
    if (!centerScrollRef.current) return;
    const rect = centerScrollRef.current.getBoundingClientRect();
    const avail = window.innerHeight - rect.top - 16; // 16px bottom padding
    setCenterHeight(Math.max(avail, 120));
  };

  useEffect(() => {
    // Wait next tick to ensure layout is ready
    setTimeout(recomputeCenterHeight, 0);
    window.addEventListener('resize', recomputeCenterHeight);
    window.addEventListener('orientationchange', recomputeCenterHeight);
    return () => {
      window.removeEventListener('resize', recomputeCenterHeight);
      window.removeEventListener('orientationchange', recomputeCenterHeight);
    };
  }, [showEpicPanel, isSettingsOpen]);

  const centerColumnStyle = { height: centerHeight ? `${centerHeight}px` : undefined, overflowY: 'auto', overscrollBehavior: 'contain' as any } as any;
  return (
    <div className="space-y-4 w-full" style={{ height: '100vh', overflow: 'hidden' }}>
      {(bootLoading || loading || !mastersLoaded || !membersLoadedFlag || !sprintsLoadedFlag) && (
        <Loader variant="overlay" size="xl" text="Loading backlog..." />
      )}
      {/* Search and Filters */}
      <div className="bg-white border border-gray-200 rounded-lg p-4">
        <div className="flex items-center gap-4">
          {/* Bulk Actions */}
          {selectedTasks.size > 0 && (
            <div className="flex items-center gap-2">
              <button
                onClick={handleDeleteSelectedTasks}
                className="flex items-center gap-2 px-3 py-2 text-sm font-medium text-red-600 bg-red-50 hover:bg-red-100 rounded-md transition-colors"
              >
                <Trash2 className="w-4 h-4" />
                Delete Selected ({selectedTasks.size})
              </button>
            </div>
          )}

          {/* Search */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="text"
              placeholder="Search backlog"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-64 h-8 pl-10 pr-3 rounded-md border border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
            />
          </div>

          {/* Assignee Filters (project members or org fallback) */}
          <AssigneeChips
            items={combinedMembers.map(m => ({ id: m.user_id, name: m.user_display_name || m.username || m.user_email || 'User' }))}
            selectedIds={selectedAssigneeIds}
            onToggle={handleAssigneeToggle}
          />

          {/* Filter button (common drawer) */}
          <div ref={filterBtnWrapRef} className="relative">
            <button onClick={() => setIsFilterOpen((v) => !v)} className="h-8 px-3 rounded-md border border-gray-300 text-gray-700 text-sm font-medium hover:bg-gray-50 flex items-center gap-2 relative">
              <Filter className="w-4 h-4" />
              Filter
              {((parentOnly ? 1 : 0) + (subtaskOnly ? 1 : 0) + selectedStatusIds.length + selectedPriorityIds.length + selectedTaskTypeIds.length + selectedAssigneeIds.length + selectedSprintIds.length + selectedLabels.length) > 0 && (
                <span className="ml-1 inline-flex items-center justify-center text-[10px] leading-none font-semibold bg-blue-600 text-white rounded-full px-1.5 py-0.5">
                  {(parentOnly ? 1 : 0) + (subtaskOnly ? 1 : 0) + selectedStatusIds.length + selectedPriorityIds.length + selectedTaskTypeIds.length + selectedAssigneeIds.length + selectedSprintIds.length + selectedLabels.length}
                </span>
              )}
            </button>
          </div>
          {/* Settings button at end */}
          <div className="ml-auto">
            <button
              ref={settingsBtnRef}
              onClick={() => setIsSettingsOpen((v) => !v)}
              title="View settings"
              className="h-8 w-8 inline-flex items-center justify-center rounded-md border border-gray-300 bg-white hover:bg-gray-50"
            >
              <SlidersHorizontal className="w-4 h-4 text-gray-700" />
            </button>
          </div>
        </div>
      </div>

      {isFilterOpen && (
        <FilterDrawer
          isOpen={isFilterOpen}
          anchorRef={filterBtnWrapRef}
          onClose={() => setIsFilterOpen(false)}
          workflowStatuses={workflowStatuses}
          taskTypes={taskTypes}
          priorities={priorities}
          sprints={sprints as any}
          members={combinedMembers as any}
          allLabels={allLabels}
          selectedStatusIds={selectedStatusIds} setSelectedStatusIds={setSelectedStatusIds}
          selectedPriorityIds={selectedPriorityIds} setSelectedPriorityIds={setSelectedPriorityIds}
          selectedTaskTypeIds={selectedTaskTypeIds} setSelectedTaskTypeIds={setSelectedTaskTypeIds}
          selectedSprintIds={selectedSprintIds} setSelectedSprintIds={setSelectedSprintIds}
          selectedAssigneeIds={selectedAssigneeIds} setSelectedAssigneeIds={setSelectedAssigneeIds}
          selectedLabels={selectedLabels} setSelectedLabels={setSelectedLabels}
          parentOnly={parentOnly} setParentOnly={setParentOnly}
          subtaskOnly={subtaskOnly} setSubtaskOnly={setSubtaskOnly}
        />
      )}

      {/* Main grid: Epic | Backlog | Settings */}
      <div
        className="grid gap-4"
        style={{
          gridTemplateColumns: `${showEpicPanel ? '288px ' : ''}1fr ${isSettingsOpen ? '360px' : ''}`.trim(),
        }}
      >
        {/* Epic column */}
        {showEpicPanel && (
          <div
            className="bg-white border border-gray-200 rounded-lg board-scroll"
            style={{
              height: centerHeight ? `${centerHeight}px` : undefined,
              overflowY: 'scroll',
              overscrollBehavior: 'contain' as any,
              scrollbarGutter: 'stable both-edges' as any,
              boxSizing: 'content-box',
              paddingRight: 2,
            }}
          >
            {/* Force a tiny overflow so the scroll thumb is always visible */}
            <div style={{ minHeight: 'calc(100% + 1px)' }}>
              <div className="flex items-center justify-between px-4 py-3 border-b">
                <div className="text-sm font-semibold text-gray-900">Epic</div>
                <button className="text-gray-500 hover:text-gray-700" onClick={() => setShowEpicPanel(false)}><CloseIcon className="w-4 h-4" /></button>
              </div>
              <div className="p-3 space-y-3">
                {(() => {
                  const epics = allTasks.filter((t: any) => {
                    const typeName = (t.task_type || '').toString().toLowerCase();
                    const typeId = t.task_type_id ? String(t.task_type_id) : '';
                    const matchType = typeName === 'epic' || (taskTypes.find(tt => String(tt.id) === typeId)?.name || '').toLowerCase() === 'epic';
                    return matchType;
                  });
                  if (epics.length === 0) return <div className="text-sm text-gray-500">No epic</div>;
                  return epics.map((e: any) => {
                    const children = allTasks.filter((t: any) => String((t as any).parent_id || '') === String(e.id));
                    const done = children.filter((t: any) => String(t.workflow_status_id || '').length > 0 && (workflowStatuses.find(ws => String(ws.id) === String(t.workflow_status_id))?.is_final)).length;
                    const total = Math.max(children.length, 1);
                    const pct = Math.round((done / total) * 100);
                    return (
                      <div key={e.id} className="space-y-1">
                        <div className="text-sm text-gray-800 truncate"><span className="inline-block w-2 h-2 rounded-sm bg-purple-400 mr-2" />{e.title}</div>
                        <div className="w-full h-2 bg-gray-200 rounded">
                          <div className="h-2 bg-green-600 rounded" style={{ width: `${pct}%` }} />
                        </div>
                      </div>
                    );
                  });
                })()}
              </div>
            </div>
          </div>
        )}

        {/* Backlog center column */}
        <div ref={centerScrollRef} style={centerColumnStyle} className="board-scroll">
          {sections.length === 0 ? (
            <div className="p-8 text-center text-gray-500">
              <p>No tasks found. Create your first task to get started!</p>
            </div>
          ) : (
            sections.map(section => (
              <div
                key={section.key}
                className={`bg-white border border-gray-200 rounded-lg mb-6 divide-y divide-gray-200 transition-shadow ${dragOverSectionKey === section.key ? 'ring-2 ring-blue-400 shadow-lg' : ''}`}
                onDragOver={(e) => handleSectionDragOver(e, section.key)}
                onDragEnter={(e) => handleSectionDragOver(e, section.key)}
                onDragLeave={(e) => handleSectionDragLeave(e, section.key)}
                onDrop={(e) => handleSectionDrop(e, section.key)}
              >
                {/* Section Header */}
                <div className="flex items-center justify-between px-4 py-3 bg-gray-50 border-b border-gray-200">
                  <div className="flex items-center gap-3">
                    {/* Section select-all checkbox (at the start) */}
                    <input
                      type="checkbox"
                      checked={section.tasks.every(t => selectedTasks.has(String(t.id))) && section.tasks.length > 0}
                      onChange={() => {
                        if(isViewerOnly) return true;
                        const allSelected = section.tasks.every(t => selectedTasks.has(String(t.id)));
                        const next = new Set(selectedTasks);
                        if (allSelected) {
                          section.tasks.forEach(t => next.delete(String(t.id)));
                        } else {
                          section.tasks.forEach(t => next.add(String(t.id)));
                        }
                        setSelectedTasks(next);
                      }}
                      className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500 focus:ring-2"
                    />
                    <button
                      onClick={() => {
                        if(!hasSprintAdminPermission) return;
                        const next = new Set(collapsedSectionKeys);
                        if (next.has(section.key)) next.delete(section.key); else next.add(section.key);
                        setCollapsedSectionKeys(next);
                      }}
                      className="w-6 h-6 inline-flex items-center justify-center border border-gray-300 rounded bg-white text-gray-700 hover:bg-gray-100"
                      title={collapsedSectionKeys.has(section.key) ? 'Expand' : 'Collapse'}
                    >
                      {collapsedSectionKeys.has(section.key) ? (
                        <ChevronRight className="w-4 h-4" />
                      ) : (
                        <ChevronDown className="w-4 h-4" />
                      )}
                    </button>
                    {section.isBacklog ? (
                      <span className="text-sm font-bold text-gray-900">{section.name}</span>
                    ) : (
                      <div className="flex items-center gap-2">
                        {editingSprintId === section.key ? (
                          <input
                            value={editingSprintName}
                            onChange={(e) => setEditingSprintName(e.target.value)}
                            onKeyDown={async (e) => { if (e.key === 'Enter') { try { await sprintAPI.updateSprint(section.key, { name: editingSprintName.trim() || 'Sprint' }); setSprints(await sprintAPI.getSprints(currentProject!.id)); } catch { } setEditingSprintId(null); } if (e.key === 'Escape') { setEditingSprintId(null); } }}
                            onBlur={async () => { try { await sprintAPI.updateSprint(section.key, { name: editingSprintName.trim() || 'Sprint' }); setSprints(await sprintAPI.getSprints(currentProject!.id)); } catch { } setEditingSprintId(null); }}
                            className="px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
                            autoFocus
                          />
                        ) : (
                          <button className="text-sm font-bold text-gray-900 hover:underline" onClick={() => { setEditingSprintId(section.key); setEditingSprintName(section.name); }}>
                            {section.name}
                          </button>
                        )}
                        {/* Edit (open modal) */}
                        {hasSprintAdminPermission && (
                        <button
                          title="Edit sprint"
                          onClick={() => {
                            const s = sprints.find(sp => String(sp.id) === String(section.key)) || null;
                            setSprintToEdit(s as any);
                            setSprintModalOpen(true);
                          }}
                          className="inline-flex items-center justify-center w-6 h-6 border border-gray-300 rounded text-gray-700 hover:bg-gray-100"
                        >
                          <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M12 20h9" />
                            <path d="M16.5 3.5a2.121 2.121 0 1 1 3 3L7 19l-4 1 1-4L16.5 3.5z" />
                          </svg>
                        </button>
                        )}
                      </div>
                    )}

                    <span className="text-sm text-gray-500">({section.tasks.length} work items)</span>
                    {section.isActive && (
                      <span className="text-xs text-green-700 bg-green-100 px-2 py-0.5 rounded">Active</span>
                    )}
                    {/* Complete Sprint Button for Active Sprints */}
                    {/* {section.isActive && !section.isBacklog && (
                    <button
                      onClick={() => handleCompleteSprint(section.key)}
                      className="ml-2 flex items-center gap-1 px-3 py-1 text-xs font-medium text-blue-600 bg-blue-50 hover:bg-blue-100 rounded transition-colors"
                      title="Complete this sprint"
                    >
                      <Check className="w-3 h-3" />
                      Complete Sprint
                    </button>
                  )} */}
                  </div>
                  <div className="flex items-center gap-2 relative" onClick={(e) => e.stopPropagation()}>
                    {section.isBacklog && hasSprintAdminPermission && (
                      <button
                        onClick={handleCreateSprint}
                        className="px-3 py-1.5 text-sm font-medium border border-gray-300 rounded hover:bg-gray-50"
                      >
                        Create sprint
                      </button>
                    )}
                    {!section.isBacklog && hasSprintAdminPermission && (
                      <>
                        {(() => {
                          const anyActive = sprints.some(sp => sp.is_active);
                          if (section.isActive) {
                            return (
                              <button
                                onClick={() => { setCompleteSprintId(section.key); setShowCompleteConfirm(true); }}
                                className="px-4 py-2 text-sm font-semibold text-white bg-blue-600 rounded hover:bg-blue-700 transition-colors"
                                title="Complete this sprint"
                              >
                                Complete sprint
                              </button>
                            );
                          }
                          return (
                            <button
                              onClick={() => handleStartSprint(section.key)}
                              className={`px-3 py-1.5 text-sm font-medium rounded transition-colors border ${anyActive ? 'text-gray-400 border-gray-300 bg-white cursor-not-allowed' : 'text-green-700 border-green-300 bg-green-100 hover:bg-green-100'}`}
                              title={anyActive ? 'Another sprint is active' : 'Start this sprint'}
                              disabled={anyActive}
                            >
                              Start Sprint
                            </button>
                          );
                        })()}
                        <button
                          className="w-8 h-8 inline-flex items-center justify-center border border-gray-300 rounded hover:bg-gray-100"
                          title="Sprint actions"
                          onClick={() => setSprintMenuOpenId(sprintMenuOpenId === section.key ? null : section.key)}
                        >
                          <MoreVertical className="w-4 h-4 text-gray-700" />
                        </button>
                        {sprintMenuOpenId === section.key && (
                          <div className="absolute right-0 top-9 z-30 w-56 bg-white border border-gray-200 rounded shadow-md">
                            <button className="w-full text-left px-4 py-2 hover:bg-gray-50" onClick={() => { moveSprintInState(section.key, 'top'); setSprintMenuOpenId(null); }}>Move sprint to top</button>
                            <button className="w-full text-left px-4 py-2 hover:bg-gray-50" onClick={() => { moveSprintInState(section.key, 'up'); setSprintMenuOpenId(null); }}>Move sprint up</button>
                            <button className="w-full text-left px-4 py-2 hover:bg-gray-50" onClick={() => { moveSprintInState(section.key, 'down'); setSprintMenuOpenId(null); }}>Move sprint down</button>
                            <button className="w-full text-left px-4 py-2 hover:bg-gray-50" onClick={() => { moveSprintInState(section.key, 'bottom'); setSprintMenuOpenId(null); }}>Move sprint to bottom</button>
                            <button className="w-full text-left px-4 py-2 hover:bg-gray-50" onClick={() => { const s = sprints.find(sp => String(sp.id) === String(section.key)) || null; setSprintToEdit(s as any); setSprintModalOpen(true); setSprintMenuOpenId(null); }}>Edit sprint</button>
                            <button className="w-full text-left px-4 py-2 text-red-600 hover:bg-red-50" onClick={() => { setSprintToDelete(section.key); setShowSprintDeleteConfirm(true); setSprintMenuOpenId(null); }}>Delete sprint</button>
                          </div>
                        )}
                      </>
                    )}
                  </div>
                </div>

                {!collapsedSectionKeys.has(section.key) && section.tasks.map((task) => {
                  const gridCols: string[] = ['24px'];
                  if (fieldWorkType) gridCols.push('20px');
                  if (fieldWorkItemKey) gridCols.push('84px');
                  // Summary column takes more space and can wrap
                  gridCols.push('2fr');
                  // Labels column slightly smaller but wraps
                  if (fieldEpic) gridCols.push('minmax(120px, 0.8fr)');
                  // Due date fits content
                  if (fieldDueDate) gridCols.push('120px');
                  // Status should be fully visible and aligned across rows
                  if (fieldStatus) gridCols.push('200px');
                  // Estimate fixed width for alignment
                  if (fieldEstimate) gridCols.push('80px');
                  // Assignee avatar
                  if (fieldAssignee) gridCols.push('64px');
                  const densityRow = compactDensity ? 'py-0.5' : 'py-2';
                  const isActive = openedTask?.id === task.id && isTaskModalOpen;
                  const isDragging = draggingTaskId === String(task.id);
                  return (
                    <div
                      key={task.id}
                      className={`group px-3 ${densityRow} transition-colors border-b border-gray-100 ${isActive ? 'bg-blue-50' : 'hover:bg-blue-50'} cursor-pointer ${isDragging ? 'opacity-50' : ''}`}
                      draggable
                      onDragStart={(e) => handleTaskDragStart(e, task, section.key)}
                      onDragEnd={handleTaskDragEnd}
                      onClick={() => {
                        setOpenedTask(task);
                        setIsTaskModalOpen(true);
                        loadComments(task.id);
                      }}
                    >
                      <div className="grid items-center" style={{ gridTemplateColumns: gridCols.join(' '), columnGap: '12px' }}>
                        {/* Checkbox */}
                        <div className={`flex-shrink-0 w-4 ${selectedTasks.has(String(task.id)) ? '' : 'opacity-0 group-hover:opacity-100 transition-opacity'}`}>
                          {!isViewerOnly && (
                          <input
                            type="checkbox"
                            checked={selectedTasks.has(String(task.id))}
                            onChange={(e) => handleTaskSelection(String(task.id), e.target.checked)}
                            className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500 focus:ring-2"
                            onClick={(e) => e.stopPropagation()}
                          />
                          )}
                        </div>

                        {/* Issue type icon */}
                        {fieldWorkType && (
                          <div className="flex items-center justify-center">
                            {(() => {
                              // Try to resolve the task type record first
                              const id = (task as any).task_type_id;
                              const nameFromTask = (task as any).task_type ? String((task as any).task_type).toLowerCase() : '';
                              const match = (Array.isArray(taskTypes) ? taskTypes : []).find(tt => {
                                if (id && String(tt.id) === String(id)) return true;
                                const n = (tt.name || '').toLowerCase();
                                return nameFromTask && n === nameFromTask;
                              });
                              const iconRaw = (match as any)?.icon || (match as any)?.icon_url || (match as any)?.image || (match as any)?.image_url;
                              const iconUrl = resolveTaskTypeIconUrl(iconRaw);
                              const n = (match?.name || nameFromTask || 'task').toLowerCase();
                              return <TaskTypeIcon url={iconUrl} name={n} className="w-4 h-4" />;
                            })()}
                          </div>
                        )}

                        {/* Issue key */}
                        {fieldWorkItemKey && (
                          <div className="pl-1 text-[12px] font-bold text-gray-800 tabular-nums">
                            {`${projectKey}-${(task as any).display_number ?? String(task.id).slice(-4)}`}
                          </div>
                        )}

                        {/* Task Title (inline editable) */}
                        <div className="flex-1 min-w-0">
                          <div className="group flex items-center gap-2">
                            {editingTaskId === String(task.id) ? (
                              <input
                                value={editingTitle}
                                onChange={(e) => setEditingTitle(e.target.value)}
                                onKeyDown={async (e) => {
                                  if (e.key === 'Enter') {
                                    if (editingTitle.trim().length >= 1) {
                                      try {
                                        await dispatch(updateTask({ id: String(task.id), data: { title: editingTitle.trim() } })).unwrap();
                                        if (currentProject?.id) {
                                          dispatch(fetchTasks({ project_id: currentProject.id }));
                                        }
                                      } catch { }
                                      setEditingTaskId(null);
                                    }
                                  } else if (e.key === 'Escape') {
                                    setEditingTaskId(null);
                                  }
                                }}
                                onBlur={async () => {
                                  if (editingTitle.trim() && editingTitle.trim() !== task.title) {
                                    try {
                                      await dispatch(updateTask({ id: String(task.id), data: { title: editingTitle.trim() } })).unwrap();
                                      if (currentProject?.id) {
                                        dispatch(fetchTasks({ project_id: currentProject.id }));
                                      }
                                    } catch { }
                                  }
                                  setEditingTaskId(null);
                                }}
                                className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
                                autoFocus
                              />
                            ) : (
                              <>
                                <span className="text-[14px] text-gray-900 whitespace-normal break-words break-all leading-5 font-medium">
                                  {task.title.replace(/[✔✓]/g, '').trim()}
                                </span>
                                {!isViewerOnly && (
                                <button
                                  className="p-1 rounded bg-gray-100 hover:bg-gray-200 border border-gray-200 opacity-0 group-hover:opacity-100 transition-opacity"
                                  title="Edit title"
                                  onClick={(e) => { e.stopPropagation(); setEditingTaskId(String(task.id)); setEditingTitle(task.title); }}
                                >
                                  <svg className="w-3.5 h-3.5 text-gray-600" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                    <path d="M12 20h9" />
                                    <path d="M16.5 3.5a2.121 2.121 0 1 1 3 3L7 19l-4 1 1-4L16.5 3.5z" />
                                  </svg>
                                </button>
                                )}
                              </>
                            )}
                          </div>
                        </div>

                        {/* Epic/Labels column */}
                        {fieldEpic && (() => {
                          const raw = (task as any).labels;
                          const labels = Array.isArray(raw)
                            ? raw
                            : (typeof raw === 'string'
                              ? raw.split(',').map((s: string) => s.trim()).filter(Boolean)
                              : []);

                          return (
                            <div className="flex flex-wrap gap-1 min-w-[120px]">
                              {labels.map((label: string, idx: number) => {
                                const isEpic = /^(epic:|epic\s+)/i.test(label);
                                const text = label.replace(/^epic:\s*/i, '').trim();
                                return (
                                  <span
                                    key={`${task.id}-label-${idx}`}
                                    className={
                                      `inline-flex items-center px-1 py-0.5 rounded text-[10px] font-medium ` +
                                      (isEpic ? 'bg-purple-100 text-purple-800' : 'bg-blue-100 text-blue-800')
                                    }
                                  >
                                    {text}
                                  </span>
                                );
                              })}
                            </div>
                          );
                        })()}

                        {/* Due date */}
                        {fieldDueDate && (
                          <div className="text-xs text-gray-700">
                            {(() => {
                              const d = (task as any).due_date;
                              if (!d) return '-';
                              try { return new Date(d).toLocaleDateString(); } catch { return '-'; }
                            })()}
                          </div>
                        )}

                        {/* Status Dropdown */}
                        {fieldStatus && (
                          <div className="w-[200px]" onClick={(e) => e.stopPropagation()}>
                            <StatusDropdown
                              disabled={isViewerOnly}
                              value={task.workflow_status_id || ''}
                              options={workflowStatuses}
                              onChange={(statusId) => handleStatusChange(task.id, statusId)}
                              placeholder="Select Status"
                              size="sm"
                            />
                          </div>
                        )}

                        {/* Estimate */}
                        {fieldEstimate && (
                          <div className="text-xs text-gray-700 tabular-nums w-[80px] text-right pr-2">
                            {((task as any).estimate ?? (task as any).story_points ?? '-')}
                          </div>
                        )}

                        {/* Assignee */}
                        {fieldAssignee && (
                          <div className="flex items-center justify-center gap-1">
                            {/* Priority Icon */}
                            {(() => {
                              const priorityId = (task as any)?.priority_id;
                              if (priorityId && priorities && priorities.length > 0) {
                                const priority = priorities.find(p => String(p.id) === String(priorityId));
                                if (priority) {
                                  return (
                                    <AvatarTooltip
                                      label={`${priority.name} Priority`}
                                      position="bottom"
                                    >
                                      <PriorityIcon
                                        priority={priority}
                                        size={16}
                                        className="cursor-pointer"
                                      />
                                    </AvatarTooltip>
                                  );
                                }
                              }
                              return null;
                            })()}

                            <div className="relative">
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setShowAssigneeDropdown(showAssigneeDropdown === task.id ? null : task.id);
                                  setAssigneeSearch('');
                                }}
                                className="flex items-center justify-center hover:bg-gray-50 rounded p-1 transition-colors"
                              >
                                {task.assigned_to ? (
                                  (() => {
                                    const mem = getMemberById(task.assigned_to.toString());
                                    const display = mem?.user_display_name || mem?.username || mem?.user_email || 'User';
                                    const initials = getInitials(display);
                                    const color = getAssigneeColor(mem?.user_id || display);
                                    return (
                                      <AvatarTooltip label={display} position="bottom">
                                        <div
                                          className={`w-7 h-7 rounded-full text-white text-[11px] font-medium flex items-center justify-center ${color}`}
                                        >
                                          {initials}
                                        </div>
                                      </AvatarTooltip>
                                    );
                                  })()
                                ) : (
                                  <AvatarTooltip label="Unassigned" position="bottom">
                                    <div className="w-7 h-7 bg-gray-200 rounded-full flex items-center justify-center">
                                      <User className="w-4 h-4 text-gray-500" />
                                    </div>
                                  </AvatarTooltip>
                                )}
                              </button>

                              {/* Assignee Dropdown */}
                              {showAssigneeDropdown === task.id && (
                                <div className="absolute top-full right-0 mt-1 w-64 bg-white border border-gray-200 rounded-lg shadow-lg z-20">
                                  {/* Search Input */}
                                  <div className="p-2 border-b border-gray-200">
                                    <input
                                      type="text"
                                      placeholder="Search assignees..."
                                      value={assigneeSearch}
                                      onChange={(e) => setAssigneeSearch(e.target.value)}
                                      className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
                                      onClick={(e) => e.stopPropagation()}
                                    />
                                  </div>

                                  {/* Assignee List (project members or org users fallback) */}
                                  <div className="max-h-48 overflow-y-auto">
                                    {currentUserId && (
                                      <button
                                        key="assign-to-me"
                                        onClick={(e) => { e.stopPropagation(); handleAssigneeChange(task.id, currentUserId); }}
                                        className={`w-full flex items-center gap-3 px-3 py-2 text-left hover:bg-blue-50 transition-colors text-blue-600 ${String(task.assigned_to ?? '') === currentUserId ? 'bg-blue-50' : ''}`}
                                      >
                                        <AvatarTooltip label={currentUserName} position="bottom">
                                          <div className={`w-6 h-6 ${currentUserColor} rounded-full flex items-center justify-center text-white text-xs font-medium`}>
                                            {currentUserInitials}
                                          </div>
                                        </AvatarTooltip>
                                        <div className="flex-1 min-w-0">
                                          <div className="text-sm font-medium text-gray-900 truncate">Assign to Me</div>
                                          <div className="text-xs text-gray-500 truncate">{currentUserName}</div>
                                        </div>
                                        {String(task.assigned_to ?? '') === currentUserId && <Check className="w-4 h-4 text-blue-600" />}
                                      </button>
                                    )}
                                    {/* Unassigned */}
                                    <button
                                      key="unassigned"
                                      onClick={(e) => { e.stopPropagation(); handleAssigneeChange(task.id, null); }}
                                      className={`w-full flex items-center gap-3 px-3 py-2 text-left hover:bg-gray-50 transition-colors ${!task.assigned_to ? 'bg-blue-50' : ''}`}
                                    >
                                      <AvatarTooltip label="Unassigned" position="bottom">
                                        <div className="w-6 h-6 bg-gray-200 rounded-full flex items-center justify-center">
                                          <User className="w-4 h-4 text-gray-500" />
                                        </div>
                                      </AvatarTooltip>
                                      <div className="flex-1 min-w-0">
                                        <div className="text-sm font-medium text-gray-900 truncate">Unassigned</div>
                                      </div>
                                    </button>
                                    {combinedMembers
                                      .filter(m => {
                                        const display = m.user_display_name || m.username || m.user_email || '';
                                        return display.toLowerCase().includes(assigneeSearch.toLowerCase());
                                      })
                                      .map((m) => {
                                        const display = m.user_display_name || m.username || m.user_email || 'User';
                                        const initials = getInitials(display);
                                        const color = getAssigneeColor(m.user_id || display);
                                        return (
                                          <button
                                            key={m.user_id}
                                            onClick={(e) => { e.stopPropagation(); handleAssigneeChange(task.id, m.user_id); }}
                                            className={`w-full flex items-center gap-3 px-3 py-2 text-left hover:bg-gray-50 transition-colors ${task.assigned_to?.toString() === m.user_id ? 'bg-blue-50' : ''}`}
                                          >
                                            <AvatarTooltip label={display} position="bottom">
                                              <div
                                                className={`w-6 h-6 ${color} rounded-full flex items-center justify-center text-white text-xs font-medium`}
                                              >
                                                {initials}
                                              </div>
                                            </AvatarTooltip>
                                            <div className="flex-1 min-w-0">
                                              <div className="text-sm font-medium text-gray-900 truncate">{display}</div>
                                              {m.user_email && (
                                                <div className="text-xs text-gray-500 truncate">{m.user_email}</div>
                                              )}
                                            </div>
                                          </button>
                                        );
                                      })}
                                  </div>
                                </div>
                              )}
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}

                {/* Inline create row */}
                {creatingSectionKey === section.key && (
                  <div className="px-3 py-3 bg-white border-t border-gray-200">
                    <div className="grid items-center" style={{ gridTemplateColumns: '24px 36px 84px 1fr 220px 180px 64px' }}>
                      {/* Empty checkbox space */}
                      <div className="w-4" />

                      {/* Type selector */}
                      <div className="relative">
                        <button
                          onClick={() => setCreateTypeOpen(v => !v)}
                          className="inline-flex items-center gap-1 px-1.5 py-1 border border-gray-300 rounded bg-white hover:bg-gray-50 text-gray-700"
                        >
                          {(() => {
                            const selected = taskTypes.find(t => String(t.id) === String(createTypeId));
                            const iconRaw = (selected as any)?.icon || (selected as any)?.icon_url || (selected as any)?.image || (selected as any)?.image_url;
                            const iconUrl = resolveTaskTypeIconUrl(iconRaw);
                            return <TaskTypeIcon url={iconUrl} name={selected?.name || 'Task'} className="w-4 h-4" />;
                          })()}
                          <ChevronDown className={`w-3 h-3 text-gray-500 transition-transform ${createTypeOpen ? 'rotate-180' : ''}`} />
                        </button>
                        {createTypeOpen && (
                          <div className="absolute z-20 mt-1 w-44 bg-white border border-gray-200 rounded shadow-lg">
                            {taskTypes.map((t) => {
                              const iconRaw = (t as any)?.icon || (t as any)?.icon_url || (t as any)?.image || (t as any)?.image_url;
                              const iconUrl = resolveTaskTypeIconUrl(iconRaw);
                              return (
                                <button
                                  key={t.id}
                                  onClick={() => { setCreateTypeId(String(t.id)); setCreateTypeOpen(false); }}
                                  className="w-full flex items-center gap-2 px-2 py-1.5 text-left hover:bg-gray-50"
                                >
                                  <TaskTypeIcon url={iconUrl} name={t.name || 'Task'} className="w-4 h-4" />
                                  <span className="text-sm text-gray-800">{t.name}</span>
                                </button>
                              );
                            })}
                          </div>
                        )}
                      </div>

                      {/* Placeholder for key */}
                      <div className="pl-1 text-[12px] font-bold text-gray-400 tabular-nums"></div>

                      {/* Title input */}
                      <div className="min-w-0">
                        <input
                          value={createTitle}
                          onChange={(e) => setCreateTitle(e.target.value)}
                          placeholder="Describe what needs to be done..."
                          className="w-full px-3 py-2 text-sm border border-blue-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
                          autoFocus
                          onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); saveCreateInSection(); } if (e.key === 'Escape') { e.preventDefault(); cancelCreate(); } }}
                        />
                      </div>

                      {/* Labels column empty */}
                      <div />

                      {/* Assignee placeholder removed */}
                      <div />

                      {/* Actions */}
                      <div className="flex items-center justify-end">
                        <button
                          onClick={saveCreateInSection}
                          disabled={!createTitle.trim()}
                          className="inline-flex items-center gap-1 px-2.5 py-1 text-sm font-medium rounded bg-gray-100 text-gray-600 hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed"
                          title="Create (Enter)"
                        >
                          Create <span className="text-gray-400">↩</span>
                        </button>
                      </div>
                    </div>
                  </div>
                )}

                {/* Section Create Button (grey footer) */}
                {!isViewerOnly && (
                <div className="px-4 py-3 bg-gray-50 border-t border-gray-200 rounded-b-lg">
                  <button
                    onClick={() => beginCreateInSection(section.key)}
                    className="flex items-center gap-2 px-2 py-1.5 text-sm font-medium text-gray-600 hover:text-gray-800 rounded-md transition-colors"
                  >
                    <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <line x1="12" y1="5" x2="12" y2="19" />
                      <line x1="5" y1="12" x2="19" y2="12" />
                    </svg>
                    Create
                  </button>
                </div>
                )}
              </div>
            ))
          )}
        </div>

        {/* Settings column */}
        {isSettingsOpen && (
          <div className="bg-white border border-gray-200 rounded-lg overflow-y-scroll" style={{ height: centerHeight ? `${centerHeight}px` : undefined, scrollbarGutter: 'stable' as any }}>
            <div className="flex items-center justify-between px-4 py-3 border-b">
              <div className="text-sm font-semibold text-gray-900">View settings</div>
              <button className="text-gray-500 hover:text-gray-700" onClick={() => setIsSettingsOpen(false)}><CloseIcon className="w-4 h-4" /></button>
            </div>
            <div className="p-4 space-y-4">
              <div className="space-y-2">
                <div className="text-xs font-semibold text-gray-700">Panels</div>
                <div className="flex items-center justify-between text-sm">
                  <span>Epic panel</span>
                  <div className="flex items-center gap-2">
                    <span className="inline-flex items-center justify-center w-5 h-5 text-[11px] font-semibold border rounded">E</span>
                    <ToggleSwitch checked={showEpicPanel} onChange={(v) => setShowEpicPanel(v)} />
                  </div>
                </div>
                <div className="flex items-center justify-between text-sm">
                  <span>Empty sprints</span>
                  <ToggleSwitch checked={showEmptySprints} onChange={(v) => setShowEmptySprints(v)} />
                </div>
              </div>
              <div className="space-y-2 pt-2 border-t">
                <div className="text-xs font-semibold text-gray-700">Density</div>
                <label className="flex items-center gap-2 text-sm">
                  <input type="radio" name="density" checked={!compactDensity} onChange={() => setCompactDensity(false)} /> Default
                </label>
                <label className="flex items-center gap-2 text-sm">
                  <input type="radio" name="density" checked={compactDensity} onChange={() => setCompactDensity(true)} /> Compact
                </label>
              </div>
              <div className="space-y-2 pt-2 border-t">
                <div className="text-xs font-semibold text-gray-700">Fields</div>
                <div className="flex items-center justify-between text-sm"><span>Work type</span><ToggleSwitch checked={fieldWorkType} onChange={(v) => setFieldWorkType(v)} /></div>
                <div className="flex items-center justify-between text-sm"><span>Work item key</span><ToggleSwitch checked={fieldWorkItemKey} onChange={(v) => setFieldWorkItemKey(v)} /></div>
                <div className="flex items-center justify-between text-sm"><span>Labels</span><ToggleSwitch checked={fieldEpic} onChange={(v) => setFieldEpic(v)} /></div>
                <div className="flex items-center justify-between text-sm"><span>Due date</span><ToggleSwitch checked={fieldDueDate} onChange={(v) => setFieldDueDate(v)} /></div>
                <div className="flex items-center justify-between text-sm"><span>Status</span><ToggleSwitch checked={fieldStatus} onChange={(v) => setFieldStatus(v)} /></div>
                <div className="flex items-center justify-between text-sm"><span>Assignee</span><ToggleSwitch checked={fieldAssignee} onChange={(v) => setFieldAssignee(v)} /></div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Create Task Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <h3 className="text-lg font-semibold mb-4">Create New Task</h3>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Task Title
                </label>
                <input
                  type="text"
                  value={newTask.title}
                  onChange={(e) => setNewTask({ ...newTask, title: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Enter task title (minimum 3 characters)..."
                  minLength={3}
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Description
                </label>
                <textarea
                  value={newTask.description}
                  onChange={(e) => setNewTask({ ...newTask, description: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  rows={3}
                  placeholder="Enter task description..."
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Type
                  </label>
                  <select
                    value={newTask.task_type_id}
                    onChange={(e) => setNewTask({ ...newTask, task_type_id: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    <option value="">Select Type</option>
                    {taskTypes.map((type) => (
                      <option key={type.id} value={type.id}>{type.name}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Priority
                  </label>
                  <select
                    value={newTask.priority_id}
                    onChange={(e) => setNewTask({ ...newTask, priority_id: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    <option value="">Select Priority</option>
                    {priorities.map((priority) => (
                      <option key={priority.id} value={priority.id}>{priority.name}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Assignee
                </label>
                <select
                  value={newTask.assignee}
                  onChange={(e) => setNewTask({ ...newTask, assignee: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="">Unassigned</option>
                  {combinedMembers.map((m) => {
                    const display = m.user_display_name || m.username || m.user_email || 'User';
                    return (
                      <option key={m.user_id} value={m.user_id}>{display}</option>
                    );
                  })}
                </select>
              </div>
            </div>

            <div className="flex justify-end gap-3 mt-6">
              <button
                onClick={() => setShowCreateModal(false)}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-md transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleCreateTask}
                className="px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-md transition-colors"
              >
                Create Task
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Confirm Delete Dialog (single or bulk) */}
      {showDeleteConfirm && (
        <DeleteConfirmDialog
          isOpen={showDeleteConfirm}
          itemName={taskToDelete
            ? (tasks.find(t => t.id === taskToDelete)?.title || 'Unknown')
            : `${selectedTasks.size} task${selectedTasks.size > 1 ? 's' : ''}`}
          itemType={taskToDelete ? 'task' : 'tasks'}
          onConfirm={taskToDelete ? handleConfirmDeleteTask : handleConfirmDeleteSelected}
          onCancel={() => {
            setShowDeleteConfirm(false);
            setTaskToDelete(null);
          }}
          isLoading={false}
        />
      )}

      {/* Sprint Create/Edit Modal */}
      {sprintModalOpen && currentProject?.id && (
        <SprintModal
          projectId={currentProject.id}
          sprint={sprintToEdit}
          onClose={() => setSprintModalOpen(false)}
          onSuccess={async () => {
            if (currentProject?.id) {
              const data = await sprintAPI.getSprints(currentProject.id);
              setSprints(data);
            }
          }}
        />
      )}

      {/* Confirm Delete Sprint Dialog */}
      {showSprintDeleteConfirm && sprintToDelete && (
        <DeleteConfirmDialog
          isOpen={showSprintDeleteConfirm}
          itemName={sprints.find(s => String(s.id) === String(sprintToDelete))?.name || 'Sprint'}
          itemType="sprint"
          onConfirm={handleConfirmDeleteSprint}
          onCancel={() => { setShowSprintDeleteConfirm(false); setSprintToDelete(null); }}
          isLoading={false}
        />
      )}

      {/* Confirm Complete Sprint */}
      {showCompleteConfirm && completeSprintId && (
        <ConfirmDialog
          isOpen={showCompleteConfirm}
          title="Complete Sprint"
          message={`Are you sure you want to complete this sprint? Incomplete tasks will be moved to backlog.`}
          confirmText="Complete"
          icon="warning"
          onConfirm={async () => { if (completeSprintId) await handleCompleteSprint(completeSprintId); }}
          onCancel={() => { setShowCompleteConfirm(false); setCompleteSprintId(null); }}
          isLoading={false}
        />
      )}

      {/* Shared Task Modal (same as Board) */}
      <TaskModal
        isViewerOnly={isViewerOnly}
        isOpen={isTaskModalOpen}
        onClose={() => {
          setIsTaskModalOpen(false);
          setOpenedTask(null);
          setComments([]);
          setActivity([]);
        }}
        task={openedTask}
        onStatusUpdate={async (taskId, newStatus) => { await handleStatusChange(taskId, newStatus); }}
        onAddComment={async (taskId, html, parentId, plainText) => {
          const contentHtml = html;
          const contentText = plainText ?? toPlainText(ensureHtml(html || ''));
          const createdComment = await commentAPI.createComment({
            task_id: taskId,
            content: contentText,
            content_html: contentHtml,
            content_text: contentText,
            parent_comment_id: parentId
          });
          await loadComments(taskId);
          return createdComment;
        }}
        onDeleteComment={async (commentId) => {
          await commentAPI.deleteComment(commentId);
          if (openedTask?.id) {
            await loadComments(openedTask.id);
          }
        }}
        onAddSubtask={async (taskId: string, title: string) => {
          try {
            await dispatch(createSubtask({ taskId, data: { title } })).unwrap();
          } catch (error) {
            console.error('Failed to add subtask:', error);
          }
        }}
        onUpdateSubtask={async (taskId: string, subtaskId: string, updates: any) => {
          void taskId;
          try {
            await dispatch(updateSubtask({ subtaskId, data: updates })).unwrap();
          } catch (error) {
            console.error('Failed to update subtask:', error);
          }
        }}
        subtasks={[]}
        relatedWork={{ blocks: [], relatesTo: [] }}
        comments={comments}
        activity={activity}
        onUpdateDescription={async (taskId, description) => { await dispatch(updateTask({ id: taskId, data: { description } })).unwrap(); }}
        onUpdateAssignee={async (taskId, assigneeId) => { await handleAssigneeChange(taskId, assigneeId); }}
        onUpdateReporter={async (taskId, reporterId) => { await handleReporterChange(taskId, reporterId); }}
        onUpdateComment={async (commentId, html, plainText) => {
          const contentHtml = html;
          const contentText = plainText ?? toPlainText(ensureHtml(html || ''));
          await commentAPI.updateComment(commentId, { content: contentText, content_html: contentHtml, content_text: contentText });
          if (openedTask?.id) {
            await loadComments(openedTask.id);
          }
        }}
        onUpdateTaskTitle={async (taskId, content) => { await dispatch(updateTask({ id: taskId, data: { title: content } })).unwrap(); }}
        onUpdateFields={async (taskId, updates) => {
          if (currentProject?.id) {
            await dispatch(fetchTasks({ project_id: currentProject.id }));
            await dispatch(fetchBacklogTasks(currentProject.id));
          }
          await dispatch(fetchTask(taskId));
          if (updates.assigned_to !== undefined && currentProject?.id) {
            try {
              const projMembers = await projectMemberAPI.getProjectMembers(currentProject.id);
              setMembers(projMembers);
            } catch (error) {
              console.error('Failed to refresh project members:', error);
            }
          }
          if (openedTask?.id === taskId) {
            try {
              const fresh = await dispatch(fetchTask(taskId)).unwrap();
              setOpenedTask(fresh);
            } catch { /* ignore */ }
          }
        }}
        projectMembers={combinedMembers as any}
        projectKey={projectKey}
        project={currentProject}
        workflowStatuses={workflowStatuses}
        onOpenSubtask={(subtask: Subtask, parent: any) => {
          setParentTaskForSubtask(parent);
          setIsTaskModalOpen(false);
          setActiveSubtask(subtask);
          setIsSubtaskModalOpen(true);
          if (parent?.id) {
            loadComments(parent.id);
          }
        }}
      />

      {/* Subtask Modal */}
      {activeSubtask && (
        <SubtaskModal
          subtask={activeSubtask}
          isOpen={isSubtaskModalOpen}
          onClose={() => {
            setIsSubtaskModalOpen(false);
            setActiveSubtask(null);
            setParentTaskForSubtask(null);
          }}
          project={currentProject}
          parentKey={`${projectKey}-${(parentTaskForSubtask as any)?.display_number ?? (openedTask as any)?.display_number ?? ''}`.replace(/-$/,'')}
          parentTitle={(parentTaskForSubtask || openedTask)?.title}
          parentTask={(parentTaskForSubtask || openedTask) as any}
          onOpenParent={(_parent) => {
            setIsSubtaskModalOpen(false);
            setActiveSubtask(null);
            setParentTaskForSubtask(null);
            if (_parent) {
              setOpenedTask(_parent);
              setIsTaskModalOpen(true);
              if (_parent.id) {
                loadComments(_parent.id);
              }
            }
          }}
          projectMembers={combinedMembers as any}
          activity={[]}
          projectKey={projectKey}
          onAddComment={async (taskId, html, parentId, plainText) => {
            const contentHtml = html;
            const contentText = plainText ?? toPlainText(ensureHtml(html || ''));
            const createdComment = await commentAPI.createComment({
              task_id: taskId,
              content: contentText,
              content_html: contentHtml,
              content_text: contentText,
              parent_comment_id: parentId
            });
            if (parentTaskForSubtask || openedTask) await loadComments((parentTaskForSubtask || openedTask)?.id || '');
            return createdComment;
          }}
          onUpdateComment={async (commentId, html, plainText) => {
            const contentHtml = html;
            const contentText = plainText ?? toPlainText(ensureHtml(html || ''));
            await commentAPI.updateComment(commentId, { content: contentText, content_html: contentHtml, content_text: contentText });
            if (parentTaskForSubtask || openedTask) await loadComments((parentTaskForSubtask || openedTask)?.id || '');
          }}
          onDeleteComment={async (commentId) => {
            await commentAPI.deleteComment(commentId);
            if (parentTaskForSubtask || openedTask) await loadComments((parentTaskForSubtask || openedTask)?.id || '');
          }}
          onSave={async (updates) => {
            await dispatch(updateSubtask({ subtaskId: activeSubtask.id, data: updates })).unwrap();
          }}
          onSubtaskUpdated={(updatedSubtask) => {
            // Update active subtask with fresh data
            setActiveSubtask(updatedSubtask);
            // Refresh task to update subtask list in TaskModal
            if (openedTask?.id) {
              dispatch(fetchTask(openedTask.id));
            }
            // Refresh backlog tasks
            if (currentProject?.id) {
              dispatch(fetchBacklogTasks(currentProject.id));
              dispatch(fetchTasks({ project_id: currentProject.id }));
            }
          }}
        />
      )}
    </div>
  );
};